import { useState } from "react";
import { ChevronRight, CheckCircle2, Clock, Copy, Eye, Trash2 } from "lucide-react";
import { Loader } from "@/components/ui/Loader";
import { cn } from "@/utils/cn";
import { formatCurrency, formatDateTime } from "@/utils/format";
import { useAuth } from "@/hooks/useAuth";
import { useSolicitacaoPagamentoAccess } from "@/hooks/useSolicitacaoPagamentoAccess";
import {
  cancelarSolicitacaoPagamento,
  canUserCancelSolicitacao,
  type SolicitacaoPagamentoDto,
} from "@/services/solicitacaoPagamento";
import { SolicitacaoPagamentoDetailModal } from "./SolicitacaoPagamentoDetailModal";
import { SolicitacaoPagamentoCancelarModal } from "./SolicitacaoPagamentoCancelarModal";
import {
  getAvatarColor,
  getDisplayStatus,
  getInitials,
  getListModeForItem,
  getStageDefForItem,
  getStageSubtext,
} from "./stageUtils";

function DisplayStatusBadge({ item }: { item: SolicitacaoPagamentoDto }) {
  const { kind, label } = getDisplayStatus(item);

  const styles: Record<typeof kind, string> = {
    andamento: "text-amber-700 bg-amber-50",
    pronta: "text-violet-700 bg-violet-50",
    concluida: "text-green-700 bg-green-50 border border-green-200",
    reprovada: "text-red-700 bg-red-50",
  };

  const Icon = kind === "concluida" ? CheckCircle2 : Clock;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full",
        styles[kind],
      )}
    >
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

function TableRow({
  item,
  onRefresh,
  onEdit,
  onDuplicate,
  canFinalizarSienge,
}: {
  item: SolicitacaoPagamentoDto;
  onRefresh: () => void;
  onEdit?: (item: SolicitacaoPagamentoDto) => void;
  onDuplicate?: (item: SolicitacaoPagamentoDto) => void;
  canFinalizarSienge?: boolean;
}) {
  const [detailOpen, setDetailOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const { user } = useAuth();
  const { canAprovarOperacional, canAprovarFinanceira, hasFullAccess } =
    useSolicitacaoPagamentoAccess();
  const userId = user?.id ?? "";

  const stage = getStageDefForItem(item);
  const StageIcon = stage.icon;
  const mode = getListModeForItem(item);

  const showCancelButton = canUserCancelSolicitacao(item, userId, {
    isAdmin: hasFullAccess,
    canAprovarOperacional,
    canAprovarFinanceira,
  });

  const handleCancel = async (motivo: string) => {
    setCancelLoading(true);
    setCancelError(null);
    try {
      await cancelarSolicitacaoPagamento(item.id, motivo);
      setCancelOpen(false);
      onRefresh();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Erro ao cancelar solicitação.";
      setCancelError(msg);
    } finally {
      setCancelLoading(false);
    }
  };

  return (
    <>
      <tr className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors">
        <td className="px-4 py-3.5">
          <div className="flex items-center gap-2.5 min-w-0">
            <div
              className={cn(
                "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold",
                getAvatarColor(item.solicitanteName),
              )}
            >
              {getInitials(item.solicitanteName)}
            </div>
            <span className="text-sm text-slate-800 font-medium truncate">
              {item.solicitanteName}
            </span>
          </div>
        </td>
        <td className="px-4 py-3.5">
          <span className="text-sm text-slate-700 truncate block max-w-[180px]">
            {item.creditorName}
          </span>
        </td>
        <td className="px-4 py-3.5 whitespace-nowrap">
          <span className="text-sm font-semibold text-slate-800 tabular-nums">
            {formatCurrency(item.valor)}
          </span>
        </td>
        <td className="px-4 py-3.5 whitespace-nowrap">
          <span className="text-sm text-slate-600">{formatDateTime(item.createdAt)}</span>
        </td>
        <td className="px-4 py-3.5">
          <div className="flex items-start gap-2 min-w-[160px]">
            <div
              className={cn(
                "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full mt-0.5",
                stage.iconBg,
              )}
            >
              <StageIcon className={cn("h-3.5 w-3.5", stage.iconColor)} />
            </div>
            <div className="min-w-0">
              <p className={cn("text-xs font-semibold leading-tight", stage.textColor)}>
                {stage.step}. {stage.title}
              </p>
              <p className="text-[10px] text-slate-400 mt-0.5">{getStageSubtext(item)}</p>
            </div>
          </div>
        </td>
        <td className="px-4 py-3.5">
          <DisplayStatusBadge item={item} />
        </td>
        <td className="px-4 py-3.5">
          <div className="flex items-center gap-0.5">
            {onDuplicate && (
              <button
                type="button"
                onClick={() => onDuplicate(item)}
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-brand-700 hover:bg-brand-50 transition-colors"
                title="Duplicar solicitação"
                aria-label="Duplicar solicitação"
              >
                <Copy className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setDetailOpen(true)}
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-brand-700 hover:bg-brand-50 transition-colors"
              title="Ver detalhes"
              aria-label="Ver detalhes da solicitação"
            >
              <Eye className="w-4 h-4" />
            </button>
            {showCancelButton && (
              <button
                type="button"
                onClick={() => setCancelOpen(true)}
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                title="Cancelar solicitação"
                aria-label="Cancelar solicitação"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setDetailOpen(true)}
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              title="Abrir solicitação"
              aria-label="Abrir solicitação"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>

      <SolicitacaoPagamentoDetailModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        item={item}
        mode={mode}
        onAction={onRefresh}
        onEdit={onEdit}
        canFinalizarSienge={canFinalizarSienge}
      />

      <SolicitacaoPagamentoCancelarModal
        open={cancelOpen}
        onClose={() => {
          setCancelOpen(false);
          setCancelError(null);
        }}
        onConfirm={handleCancel}
        loading={cancelLoading}
        error={cancelError}
      />
    </>
  );
}

export function SolicitacaoPagamentoTable({
  items,
  emptyMessage,
  onRefresh,
  onEdit,
  onDuplicate,
  canFinalizarSienge,
  loading,
}: {
  items: SolicitacaoPagamentoDto[];
  emptyMessage: string;
  onRefresh: () => void;
  onEdit?: (item: SolicitacaoPagamentoDto) => void;
  onDuplicate?: (item: SolicitacaoPagamentoDto) => void;
  canFinalizarSienge?: boolean;
  loading?: boolean;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[900px]">
        <thead>
          <tr className="border-b border-slate-200">
            {[
              "Solicitante",
              "Fornecedor",
              "Valor",
              "Data",
              "Etapa atual",
              "Status",
              "Ações",
            ].map((col) => (
              <th
                key={col}
                className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-400"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={7} className="px-4 py-12">
                <div className="flex justify-center">
                  <Loader size="md" label="Carregando solicitações..." />
                </div>
              </td>
            </tr>
          ) : items.length === 0 ? (
            <tr>
              <td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-500">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            items.map((item) => (
              <TableRow
                key={item.id}
                item={item}
                onRefresh={onRefresh}
                onEdit={onEdit}
                onDuplicate={onDuplicate}
                canFinalizarSienge={canFinalizarSienge}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
